import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity via a dropdown.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} dropdownTrigger
 * @property {HTMLElement} dropdownList
 * @property {HTMLElement[]} dropdownOptions
 * @property {HTMLElement} dropdownValue
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorDropdownComponent extends Component {
  /** @type {((event: MouseEvent) => void) | undefined} */
  #boundClickOutside;

  /**
   * Toggles the dropdown open/closed.
   * @param {Event} event
   */
  toggleDropdown(event) {
    if (event.target instanceof HTMLButtonElement && event.target.disabled) return;

    const isOpen = this.dataset.open === 'true';

    if (isOpen) {
      this.#closeDropdown();
    } else {
      this.#openDropdown();
    }
  }

  /**
   * Opens the dropdown and focuses the selected option.
   */
  #openDropdown() {
    this.dataset.open = 'true';
    const { dropdownList, dropdownTrigger } = this.refs;

    if (dropdownList) {
      dropdownList.hidden = false;
    }

    if (dropdownTrigger) {
      dropdownTrigger.setAttribute('aria-expanded', 'true');
    }

    this.#focusSelectedOption();
    this.#bindClickOutside();
  }

  /**
   * Closes the dropdown.
   */
  #closeDropdown() {
    this.dataset.open = 'false';
    const { dropdownList, dropdownTrigger } = this.refs;

    if (dropdownList) {
      dropdownList.hidden = true;
    }

    if (dropdownTrigger) {
      dropdownTrigger.setAttribute('aria-expanded', 'false');
      dropdownTrigger.focus();
    }

    this.#unbindClickOutside();
  }

  /**
   * Binds a click-outside listener to close the dropdown.
   */
  #bindClickOutside() {
    const handler = (this.#boundClickOutside ??= this.#handleClickOutside.bind(this));
    document.addEventListener('click', handler, true);
  }

  /**
   * Unbinds the click-outside listener.
   */
  #unbindClickOutside() {
    const handler = this.#boundClickOutside;

    if (handler) {
      document.removeEventListener('click', handler, true);
    }
  }

  /**
   * Handles clicks outside the component to close the dropdown.
   * @param {MouseEvent} event
   */
  #handleClickOutside(event) {
    const target = event.target instanceof Node ? event.target : null;

    if (target && this.contains(target)) return;

    this.#closeDropdown();
  }

  /**
   * Handles option selection (click).
   * @param {Event} event
   */
  selectOption(event) {
    const option = event.target instanceof HTMLElement ? event.target.closest('[data-value]') : null;

    if (!(option instanceof HTMLElement) || option.dataset.value == null) return;

    const value = parseInt(option.dataset.value, 10);

    this.#setValue(value);
    this.#closeDropdown();
  }

  /**
   * Sets the quantity value and updates UI and hidden input.
   * @param {number} value
   */
  #setValue(value) {
    const { quantityInput, dropdownValue, dropdownOptions } = this.refs;

    if (!quantityInput) return;

    const min = parseInt(quantityInput.getAttribute('min') ?? '1', 10) || 1;
    const max = parseInt(quantityInput.getAttribute('max') ?? '999', 10) || 999;
    const clamped = Math.min(Math.max(value, min), max);

    quantityInput.value = String(clamped);

    if (dropdownValue) {
      dropdownValue.textContent = String(clamped);
    }

    for (const opt of dropdownOptions ?? []) {
      const optValue = parseInt(opt.getAttribute('data-value') ?? '0', 10);
      const selected = optValue === clamped;

      opt.classList.toggle('quantity-dropdown__option--selected', selected);
      opt.setAttribute('aria-selected', selected ? 'true' : 'false');
    }

    this.#onQuantityChange();
  }

  /**
   * Focuses the currently selected option in the list.
   */
  #focusSelectedOption() {
    const selected = this.refs.dropdownOptions?.find((el) => el.getAttribute('aria-selected') === 'true');

    if (selected) {
      selected.focus();
    }
  }

  /**
   * Handles keydown on the trigger (Enter, Space, ArrowDown, ArrowUp).
   * @param {KeyboardEvent} event
   */
  handleTriggerKeydown(event) {
    const { key } = event;

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      this.toggleDropdown(event);
      return;
    }

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();

      if (this.dataset.open !== 'true') {
        this.#openDropdown();
      } else {
        this.#navigateOptions(key);
      }
    }
  }

  /**
   * Handles keydown on an option (Enter, Space, ArrowDown, ArrowUp, Escape).
   * @param {KeyboardEvent} event
   */
  handleOptionKeydown(event) {
    const option = event.target instanceof HTMLElement ? event.target.closest('[data-value]') : null;

    if (!option) return;

    const { key } = event;

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      const value = parseInt(option.getAttribute('data-value') ?? '0', 10);
      this.#setValue(value);
      this.#closeDropdown();
      return;
    }

    if (key === 'Escape') {
      event.preventDefault();
      this.#closeDropdown();
      return;
    }

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      this.#navigateOptions(key);
    }
  }

  /**
   * Moves focus and optional selection in the option list.
   * @param {string} direction - 'ArrowDown' | 'ArrowUp'
   */
  #navigateOptions(direction) {
    const options = this.refs.dropdownOptions;

    if (!options?.length) return;

    const current = this.querySelector('.quantity-dropdown__option:focus');
    const index = current ? options.indexOf(/** @type {HTMLElement} */ (current)) : -1;
    const nextIndex =
      direction === 'ArrowDown' ? Math.min(index + 1, options.length - 1) : Math.max(index - 1, 0);

    options[nextIndex]?.focus();
  }

  /**
   * Dispatches the quantity change event for cart/form listeners.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    if (!quantityInput) return;

    const newValue = parseInt(quantityInput.value, 10);
    const cartLine = quantityInput.dataset.cartLine ? Number(quantityInput.dataset.cartLine) : undefined;

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, cartLine));
  }

  /**
   * Gets the quantity input (required for parent components that read it).
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error(
        'Missing <input ref="quantityInput" /> inside <quantity-selector-dropdown-component />'
      );
    }

    return this.refs.quantityInput;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindClickOutside();
  }
}

if (!customElements.get('quantity-selector-dropdown-component')) {
  customElements.define('quantity-selector-dropdown-component', QuantitySelectorDropdownComponent);
}
